import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Snackbar,
    Alert,
    Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/GridLegacy';
import { Users, BookOpen, Activity, Settings, Plus, UserPlus, Mail, KeyRound, ChevronRight, User, Bell, CreditCard } from 'lucide-react';
import ParentNavbar from '../student/ParentNavbar';
import { useNavigate } from 'react-router-dom';
import { initiateLinkStudent, verifyLinkStudent, getChildren, getParentNotifications } from '../../services/parentService';
import notificationBell from '../../assets/audios/notificationBell.wav';
import SubscriptionModal from '../../components/subscription/SubscriptionModal';

interface Student {
    _id: string;
    name: string;
    lastName: string;
    email: string;
    currentGrade?: string;
    subscriptionStatus?: string;
}

interface ParentNotification {
    _id: string;
    message: string;
    promptText?: string;
    createdAt: string;
    read: boolean;
}

const ParentDashboard: React.FC = () => {
    const navigate = useNavigate();
    const seenNotificationIdsRef = useRef<Set<string>>(new Set());
    const hasInitializedNotificationsRef = useRef(false);
    const notificationAudioRef = useRef<HTMLAudioElement | null>(null);

    // State for Dialog & Linking Process
    const [openLinkDialog, setOpenLinkDialog] = useState(false);
    const [activeStep, setActiveStep] = useState<'email' | 'otp'>('email');
    const [studentEmail, setStudentEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // innovative feedback state
    const [isEmailSent, setIsEmailSent] = useState(false);

    // Data State
    const [children, setChildren] = useState<Student[]>([]);
    const [loadingChildren, setLoadingChildren] = useState(true);
    const [notifications, setNotifications] = useState<ParentNotification[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(true);

    // Subscription modal state
    const [subscriptionChild, setSubscriptionChild] = useState<Student | null>(null);

    const playNotificationSound = useCallback(() => {
        if (typeof window === 'undefined') return;

        if (!notificationAudioRef.current) {
            const audio = new Audio(notificationBell);
            audio.preload = 'auto';
            notificationAudioRef.current = audio;
        }

        const audio = notificationAudioRef.current;
        if (!audio) return;

        audio.currentTime = 0;
        audio.play().catch(() => {
            // Browser blocked autoplay audio. Ignore silently.
        });
    }, []);

    useEffect(() => {
        fetchChildren();
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchNotifications = async () => {
            try {
                const data = await getParentNotifications();
                const latestNotifications: ParentNotification[] = data.notifications || [];
                const latestIds = new Set(latestNotifications.map((note) => note._id));

                if (hasInitializedNotificationsRef.current) {
                    const hasNewNotification = latestNotifications.some(
                        (note) => !seenNotificationIdsRef.current.has(note._id),
                    );
                    if (hasNewNotification) {
                        playNotificationSound();
                    }
                }

                seenNotificationIdsRef.current = latestIds;
                hasInitializedNotificationsRef.current = true;

                if (isMounted) setNotifications(latestNotifications);
            } catch (err) {
                console.error('Failed to load notifications', err);
            } finally {
                if (isMounted) setLoadingNotifications(false);
            }
        };

        fetchNotifications();
        const interval = setInterval(fetchNotifications, 15000);
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [playNotificationSound]);

    useEffect(() => {
        return () => {
            if (notificationAudioRef.current) {
                notificationAudioRef.current.pause();
                notificationAudioRef.current.currentTime = 0;
                notificationAudioRef.current = null;
            }
        };
    }, []);

    const fetchChildren = async () => {
        try {
            const data = await getChildren();
            setChildren(data.children || []);
        } catch (err) {
            console.error('Failed to load children', err);
        } finally {
            setLoadingChildren(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenLinkDialog(false);
        setActiveStep('email');
        setStudentEmail('');
        setOtp('');
        setError(null);
        setIsEmailSent(false);
        setIsLoading(false);
    };

    const handleInitiateLink = async () => {
        if (!studentEmail) {
            setError('Please enter a valid email address.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            await initiateLinkStudent(studentEmail);
            setActiveStep('otp');
            setIsEmailSent(true);
            setSnackbar({ open: true, message: 'OTP sent to student email', severity: 'success' });
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send OTP. Check if email is correct.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyLink = async () => {
        if (!otp) {
            setError('Please enter the OTP.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            await verifyLinkStudent(studentEmail, otp);
            setSnackbar({ open: true, message: 'Student account linked successfully!', severity: 'success' });
            handleCloseDialog();
            fetchChildren(); // Refresh list
        } catch (err: any) {
            setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStudentClick = (student: Student) => {
        navigate(`/parent/student/${student._id}`, {
            state: { studentName: `${student.name} ${student.lastName}` }
        });
    };

    return (
        <Box sx={(theme) => ({ minHeight: '100vh', backgroundColor: theme.palette.background.default })}>
            <ParentNavbar />

            <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
                <Box sx={{
                    mb: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 2.5, sm: 0 }
                }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: 'text.primary', mb: 1, fontSize: { xs: '1.8rem', sm: '2.125rem' } }}>
                            Parent Dashboard
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Monitor and manage your children's learning journey
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<UserPlus size={18} />}
                        onClick={() => setOpenLinkDialog(true)}
                        fullWidth={false}
                        sx={{
                            backgroundColor: '#6c5ce7',
                            '&:hover': { backgroundColor: '#5b4bc4' },
                            borderRadius: '12px',
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            fontWeight: 600,
                            width: { xs: '100%', sm: 'auto' },
                            boxShadow: '0 4px 14px 0 rgba(108, 92, 231, 0.39)'
                        }}
                    >
                        Link Student Account
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {/* Children List Section */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={(theme) => ({
                            p: 0,
                            borderRadius: 4,
                            overflow: 'hidden',
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 14px 40px rgba(0, 0, 0, 0.35)'
                                : '0 4px 20px rgba(0,0,0,0.05)'
                        })}>
                            <Box sx={(theme) => ({ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' })}>
                                <Users size={20} style={{ marginRight: '10px', color: '#6c5ce7' }} />
                                <Typography variant="h6" fontWeight="600">My Children</Typography>
                            </Box>

                            {loadingChildren ? (
                                <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={28} sx={{ color: '#6c5ce7' }} />
                                </Box>
                            ) : children.length === 0 ? (
                                <Box sx={{ p: 6, textAlign: 'center' }}>
                                    <Box sx={(theme) => ({
                                        width: 64, height: 64, borderRadius: '50%', backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
                                    })}>
                                        <UserPlus size={32} color="#6c5ce7" />
                                    </Box>
                                    <Typography variant="h6" color="text.primary" gutterBottom>
                                        No students linked yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                                        Link your child's account to view their progress, grades, and manage their subscription.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setOpenLinkDialog(true)}
                                        sx={{ borderRadius: '8px', textTransform: 'none', borderColor: '#6c5ce7', color: '#6c5ce7' }}
                                    >
                                        Link Now
                                    </Button>
                                </Box>
                            ) : (
                                <List sx={{ p: 0 }}>
                                    {children.map((student, index) => (
                                        <React.Fragment key={student._id}>
                                            {index > 0 && <Box sx={(theme) => ({ height: 1, backgroundColor: theme.palette.divider, ml: 9 })} />}
                                            <ListItemButton
                                                onClick={() => handleStudentClick(student)}
                                                sx={(theme) => ({
                                                    p: { xs: 2, sm: 2.5 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06) },
                                                    transition: 'background-color 0.2s'
                                                })}
                                            >
                                                <ListItemAvatar sx={{ minWidth: { xs: 56, sm: 64 } }}>
                                                    <Avatar sx={{ bgcolor: '#a29bfe', width: 48, height: 48 }}>
                                                        {student.name.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>

                                                {/* Center Content: Name, Email (truncated), Grade */}
                                                <Box sx={{ flexGrow: 1, minWidth: 0, pr: { xs: 1, sm: 2 } }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="600"
                                                        color="text.primary"
                                                        noWrap
                                                        sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                                                    >
                                                        {student.name} {student.lastName}
                                                    </Typography>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5, width: '100%', overflow: 'hidden', color: 'text.secondary' }}>
                                                        <Mail size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {student.email}
                                                        </Typography>
                                                    </Box>

                                                    {student.currentGrade && (
                                                        <Box>
                                                            <Typography variant="caption" sx={(theme) => ({
                                                                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#eff0f6',
                                                                color: 'text.primary',
                                                                px: 1,
                                                                py: 0.3,
                                                                borderRadius: 1.5,
                                                                fontWeight: 500,
                                                                display: 'inline-block'
                                                            })}>
                                                                Grade {student.currentGrade}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* Right Content: Status, Chevron */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Box sx={{ textAlign: 'right', mr: { xs: 0.5, sm: 2 } }}>
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                            sx={{ display: { xs: 'none', sm: 'block' } }}
                                                        >
                                                            Status
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="700"
                                                            color={student.subscriptionStatus === 'active' ? 'success.main' : 'text.secondary'}
                                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                        >
                                                            {student.subscriptionStatus === 'active' ? 'Active' : 'Free'}
                                                        </Typography>
                                                    </Box>
                                                    {student.subscriptionStatus !== 'active' && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<CreditCard size={14} />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSubscriptionChild(student);
                                                            }}
                                                            sx={{
                                                                mr: { xs: 0.5, sm: 1.5 },
                                                                backgroundColor: '#00b894',
                                                                '&:hover': { backgroundColor: '#00a382' },
                                                                borderRadius: '8px',
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                px: { xs: 1, sm: 2 },
                                                                py: 0.6,
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: '0 2px 8px rgba(0,184,148,0.3)'
                                                            }}
                                                        >
                                                            Buy Plan
                                                        </Button>
                                                    )}
                                                    <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
                                                        <ChevronRight size={20} />
                                                    </Box>
                                                </Box>
                                            </ListItemButton>
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>

                    {/* Quick Stats Placeholder */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={(theme) => ({
                            p: 3,
                            borderRadius: 4,
                            mb: 3,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 14px 40px rgba(0, 0, 0, 0.35)'
                                : '0 4px 20px rgba(0,0,0,0.05)'
                        })}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Bell size={20} style={{ marginRight: '10px', color: '#ff9f43' }} />
                                <Typography variant="h6" fontWeight="600">Notifications</Typography>
                            </Box>

                            {loadingNotifications ? (
                                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: '#ff9f43' }} />
                                </Box>
                            ) : notifications.length === 0 ? (
                                <Box sx={(theme) => ({
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.16) : '#fff7ed',
                                    borderRadius: 3,
                                    p: 2.5,
                                    textAlign: 'center',
                                    border: `1px dashed ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`
                                })}>
                                    <Typography variant="body2" color="text.secondary">
                                        No new notifications yet.
                                    </Typography>
                                </Box>
                            ) : (
                                <List sx={(theme) => ({
                                    p: 0,
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    pr: 1,
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-track': {
                                        background: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : '#f1f1f1',
                                        borderRadius: '4px'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.2) : '#dcdde1',
                                        borderRadius: '4px'
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.35) : '#7f8fa6'
                                    }
                                })}>
                                    {notifications.map((note) => (
                                        <ListItem key={note._id} sx={{ px: 0, py: 1 }}>
                                            <ListItemAvatar>
                                                <Avatar sx={(theme) => ({
                                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.22) : '#ffeaa7',
                                                    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#d35400',
                                                    width: 36,
                                                    height: 36
                                                })}>
                                                    <Bell size={16} />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" fontWeight={note.read ? 400 : 600} color="text.primary">
                                                        {note.message}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(note.createdAt).toLocaleString()}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Link Student Dialog */}
                <Dialog
                    open={openLinkDialog}
                    onClose={handleCloseDialog}
                    PaperProps={{
                        sx: (theme) => ({
                            borderRadius: 3,
                            width: '100%',
                            maxWidth: 450,
                            border: `1px solid ${theme.palette.divider}`
                        })
                    }}
                >
                    <DialogTitle sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2, pt: 3 })}>
                        <Typography variant="h6" fontWeight="bold" textAlign="center">
                            {activeStep === 'email' ? 'Link Student Account' : 'Verify Account'}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 4, pb: 2, px: 4 }}>
                        {activeStep === 'email' ? (
                            <Box>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                                    Enter the email address registered with your child's student account. We'll send an OTP to verify.
                                </Typography>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Student Email Address"
                                    variant="outlined"
                                    value={studentEmail}
                                    onChange={(e) => setStudentEmail(e.target.value)}
                                    error={!!error}
                                    helperText={error}
                                    InputProps={{
                                        startAdornment: <Mail size={18} color="currentColor" style={{ marginRight: 10, color: 'inherit' }} />
                                    }}
                                    sx={{ mb: 1 }}
                                />
                            </Box>
                        ) : (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                    OTP sent to <strong>{studentEmail}</strong>
                                </Alert>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                                    Please enter the 6-digit code sent to the student's email to verify ownership.
                                </Typography>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Enter OTP"
                                    variant="outlined"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    error={!!error}
                                    helperText={error}
                                    InputProps={{
                                        startAdornment: <KeyRound size={18} color="currentColor" style={{ marginRight: 10, color: 'inherit' }} />
                                    }}
                                />
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => setActiveStep('email')}
                                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                                    >
                                        Change Email
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 4, pb: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={activeStep === 'email' ? handleInitiateLink : handleVerifyLink}
                            disabled={isLoading}
                            sx={{
                                py: 1.2,
                                borderRadius: 2,
                                backgroundColor: '#6c5ce7',
                                '&:hover': { backgroundColor: '#5b4bc4' },
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600
                            }}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : (activeStep === 'email' ? 'Send OTP' : 'Verify & Link')}
                        </Button>
                        <Button
                            variant="text"
                            fullWidth
                            onClick={handleCloseDialog}
                            sx={{ color: 'text.secondary', textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Subscription Modal for Child */}
                <SubscriptionModal
                    isOpen={!!subscriptionChild}
                    onClose={() => setSubscriptionChild(null)}
                    childId={subscriptionChild?._id}
                    childName={subscriptionChild ? `${subscriptionChild.name} ${subscriptionChild.lastName}` : undefined}
                />
            </Container>
        </Box>
    );
};

export default ParentDashboard;
